import React, { useState, useRef, useEffect } from 'react';
import styles from './GlobalTabs.module.css';

type TabItem = {
  label: string;
  content: React.ReactNode;
};

type GlobalTabsProps = {
  tabs: TabItem[];
  initialIndex?: number;
  onChange?: (index: number) => void;
};

export default function GlobalTabs({ tabs, initialIndex = 0, onChange }: GlobalTabsProps) {
  const [selected, setSelected] = useState(initialIndex);
  const indicatorRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const tabListRef = useRef<HTMLDivElement>(null);

  // Sync selected state with initialIndex changes (for URL-based tab changes)
  useEffect(() => {
    setSelected(initialIndex);
  }, [initialIndex]);

  useEffect(() => {
    const currentTab = tabRefs.current[selected];
    const indicator = indicatorRef.current;
    const tabList = tabListRef.current;

    if (currentTab && indicator) {
      const { offsetLeft, offsetWidth } = currentTab;
      indicator.style.left = `${offsetLeft}px`;
      indicator.style.width = `${offsetWidth}px`;
    }

    // Smooth scroll to ensure selected tab is visible
    if (currentTab && tabList) {
      const tabListRect = tabList.getBoundingClientRect();
      const tabRect = currentTab.getBoundingClientRect();
      
      // Check if tab is outside the visible area
      if (tabRect.left < tabListRect.left) {
        // Tab is to the left of visible area
        tabList.scrollTo({
          left: tabList.scrollLeft + (tabRect.left - tabListRect.left),
          behavior: 'smooth'
        });
      } else if (tabRect.right > tabListRect.right) {
        // Tab is to the right of visible area
        tabList.scrollTo({
          left: tabList.scrollLeft + (tabRect.right - tabListRect.right),
          behavior: 'smooth'
        });
      }
    }
  }, [selected, tabs]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.tabList} ref={tabListRef}>
        {tabs.map((tab, index) => (
          <button
            key={index}
            type="button"
            ref={el => { tabRefs.current[index] = el }}
            className={`${styles.tab} ${selected === index ? styles.active : ''}`}
            onClick={() => {
              setSelected(index);
              onChange?.(index);
            }}
          >
            {tab.label}
          </button>
        ))}
        <div className={styles.indicator} ref={indicatorRef} />
      </div>
      <div className={styles.tabContent} key={selected}>
        {tabs[selected]?.content}
      </div>
    </div>
  );
}